/**
 * Wizard state lives entirely in React (per design.md Decision 1). The reducer
 * keeps every transition inspectable and lets `Back` navigation preserve form
 * state for free.
 *
 * Action types:
 *   OPEN, CANCEL, SELECT_TYPE, BACK, GOTO_CONFIRM, SET_FILE, SET_URL, SET_TITLE,
 *   SET_CATEGORY, ADD_TAG, REMOVE_TAG, TOGGLE_AUDIENCE, TOGGLE_ALL_AUDIENCE,
 *   SUBMIT_START, SUBMIT_FAIL, SET_FIELD_ERROR,
 *   START_REPLACING, CANCEL_REPLACING        (slice 7, edit mode)
 */

export const ROLE_KEYS = ['serviceProvider', 'customer', 'salesRepresentative', 'imbera']

export const ROLE_AUDIENCE_CODES = {
	serviceProvider: 'SERVICE_PROVIDER',
	customer: 'CUSTOMER',
	salesRepresentative: 'SALES_REPRESENTATIVE',
	imbera: 'IMBERA'
}

const CODE_TO_ROLE_KEY = Object.entries(ROLE_AUDIENCE_CODES).reduce(
	(acc, [k, v]) => ({ ...acc, [v]: k }),
	{}
)

const TYPE_INT_TO_LABEL = { 1: 'document', 2: 'image', 3: 'video', 4: 'link' }

export const initialWizardState = {
	currentStep: 1,
	type: null,                    // 'document' | 'image' | 'video' | 'link'
	file: null,                    // File | null
	fileError: null,               // string | null
	externalUrl: '',
	title: '',
	categoryId: null,              // int | null
	tags: [],                      // Array<{ Id, Name }>
	audience: {
		serviceProvider: false,
		customer: false,
		salesRepresentative: false,
		imbera: false
	},
	isSubmitting: false,
	fieldErrors: {},               // { title?, categoryId?, file?, externalUrl?, audience? }
	// Edit mode (slice 7). For create paths these stay at their defaults.
	originalFilename: '',          // display-only; the existing file's name
	isReplacing: false             // true when the admin clicked "Replace file"
}

/**
 * Build a wizard initial-state object from an existing asset (slice 7 edit
 * mode). The asset shape mirrors what the list endpoint returns.
 *   - `Type` is int → mapped to the string label the reducer uses.
 *   - `AudienceCodes` is an array of strings. ["ALL"] expands to every
 *     checkbox checked; otherwise individual codes map back to role keys.
 *   - `Tags` is already in the wizard's `{Id,Name}` shape.
 *   - `file` stays null — we don't pre-load the bytes. The original filename
 *     is preserved for the "Current file:" display.
 *   - `currentStep` starts at 2 because the type is locked; landing on
 *     Step 1 would just be the locked-type display.
 */
export const seedFromAsset = (asset) => {
	if (!asset) return { ...initialWizardState }

	const audience = ROLE_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {})
	const codes = Array.isArray(asset.AudienceCodes) ? asset.AudienceCodes : []
	if (codes.includes('ALL')) {
		ROLE_KEYS.forEach((k) => { audience[k] = true })
	} else {
		codes.forEach((code) => {
			const key = CODE_TO_ROLE_KEY[code]
			if (key) audience[key] = true
		})
	}

	return {
		...initialWizardState,
		currentStep: 2,
		type: TYPE_INT_TO_LABEL[asset.Type] || null,
		title: asset.Title || '',
		categoryId: asset.CategoryId || null,
		tags: Array.isArray(asset.Tags) ? asset.Tags.slice() : [],
		audience,
		externalUrl: asset.ExternalUrl || '',
		originalFilename: asset.OriginalFilename || ''
	}
}

export const wizardReducer = (state, action) => {
	switch (action.type) {
		case 'OPEN':
			return { ...initialWizardState }
		case 'CANCEL':
			return { ...initialWizardState }
		case 'SELECT_TYPE':
			return { ...state, type: action.value, currentStep: 2, file: null, fileError: null, externalUrl: '' }
		case 'BACK':
			return { ...state, currentStep: Math.max(1, state.currentStep - 1) }
		case 'GOTO_CONFIRM':
			return { ...state, currentStep: 3 }
		case 'SET_FILE':
			return { ...state, file: action.value, fileError: action.error || null, fieldErrors: { ...state.fieldErrors, file: undefined } }
		case 'SET_URL':
			return { ...state, externalUrl: action.value, fieldErrors: { ...state.fieldErrors, externalUrl: undefined } }
		case 'SET_TITLE':
			return { ...state, title: action.value, fieldErrors: { ...state.fieldErrors, title: undefined } }
		case 'SET_CATEGORY':
			return { ...state, categoryId: action.value, fieldErrors: { ...state.fieldErrors, categoryId: undefined } }
		case 'ADD_TAG':
			if (state.tags.some((t) => t.Id === action.tag.Id)) return state
			return { ...state, tags: [...state.tags, action.tag] }
		case 'REMOVE_TAG':
			return { ...state, tags: state.tags.filter((t) => t.Id !== action.tagId) }
		case 'TOGGLE_AUDIENCE':
			return { ...state, audience: { ...state.audience, [action.role]: !state.audience[action.role] }, fieldErrors: { ...state.fieldErrors, audience: undefined } }
		case 'TOGGLE_ALL_AUDIENCE': {
			const everyoneOn = ROLE_KEYS.every((k) => state.audience[k])
			const next = !everyoneOn
			return {
				...state,
				audience: ROLE_KEYS.reduce((acc, k) => ({ ...acc, [k]: next }), {}),
				fieldErrors: { ...state.fieldErrors, audience: undefined }
			}
		}
		case 'SUBMIT_START':
			return { ...state, isSubmitting: true, fieldErrors: {} }
		case 'SUBMIT_FAIL':
			return { ...state, isSubmitting: false, fieldErrors: action.errors || {} }
		case 'SET_FIELD_ERROR':
			return { ...state, fieldErrors: { ...state.fieldErrors, [action.field]: action.message } }
		case 'START_REPLACING':
			return { ...state, isReplacing: true, file: null, fileError: null, fieldErrors: { ...state.fieldErrors, file: undefined } }
		case 'CANCEL_REPLACING':
			return { ...state, isReplacing: false, file: null, fileError: null, fieldErrors: { ...state.fieldErrors, file: undefined } }
		default:
			return state
	}
}

// ── Derived selectors ──────────────────────────────────────────────────────

/** True when every role checkbox is checked. The `All` UI control derives from this. */
export const isAllChecked = (state) => ROLE_KEYS.every((k) => state.audience[k])

/** True if the user has modified anything from the initial state. */
export const hasAnyInput = (state) =>
	state.type !== null ||
	state.file !== null ||
	state.externalUrl !== '' ||
	state.title !== '' ||
	state.categoryId !== null ||
	state.tags.length > 0 ||
	ROLE_KEYS.some((k) => state.audience[k])

/** Convert internal audience state to wire-format audience codes. */
export const audienceCodesFromState = (state) => {
	if (isAllChecked(state)) return ['ALL']
	return ROLE_KEYS.filter((k) => state.audience[k]).map((k) => ROLE_AUDIENCE_CODES[k])
}

/** Step-2 validation. Returns null when valid, or an error map. */
export const validateStep2 = (state) => {
	const errs = {}
	if (!state.title || state.title.trim().length === 0) errs.title = 'Title is required.'
	else if (state.title.length > 255) errs.title = 'Title must be 255 characters or fewer.'
	if (!state.categoryId) errs.categoryId = 'Category is required.'
	if (state.type === 'link') {
		if (!state.externalUrl || state.externalUrl.trim().length === 0) errs.externalUrl = 'URL is required.'
		else if (!isWellFormedUrl(state.externalUrl)) errs.externalUrl = 'Enter a valid http or https URL.'
	} else {
		// Edit mode + user hasn't chosen to replace = keep the existing file.
		// `originalFilename` is the signal that the wizard was seeded from an
		// asset (initialWizardState leaves it empty), so we don't require a
		// new file in that case.
		const isEditModeNoReplace = state.originalFilename && !state.isReplacing
		if (!isEditModeNoReplace) {
			if (!state.file) errs.file = 'A file is required.'
			else if (state.file.size > 50 * 1024 * 1024) errs.file = 'File exceeds the 50 MB limit.'
		}
	}
	if (audienceCodesFromState(state).length === 0) errs.audience = 'Select at least one audience.'
	return Object.keys(errs).length === 0 ? null : errs
}

const isWellFormedUrl = (s) => {
	try {
		const u = new URL(s)
		return u.protocol === 'http:' || u.protocol === 'https:'
	} catch (e) {
		return false
	}
}
